import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/components/forms.css';
import './EmailManagement.css';

export default function EmailManagement() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [sortBy, setSortBy] = useState('last_logged_in');
    const [sortOrder, setSortOrder] = useState('desc');
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailContent, setEmailContent] = useState('');
    const [sending, setSending] = useState(false);
    const { logout } = useAuth();

    useEffect(() => {
        fetchUsers();
    }, [sortBy, sortOrder]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/email/users?sortBy=${sortBy}&sortOrder=${sortOrder}`,
                { withCredentials: true }
            );
            setUsers(response.data);
            setFilteredUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            if (error.response?.status === 401) {
                logout();
            } else {
                setError('Kunne ikke laste brukere');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let filtered = [...users];

        // Apply device filter
        if (deviceFilter !== 'all') {
            filtered = filtered.filter(user => {
                if (deviceFilter === 'android') return user.device === 'android';
                if (deviceFilter === 'ios') return user.device === 'ios';
                if (deviceFilter === 'unknown') return !user.device || user.device === 'unknown';
                return true;
            });
        }

        setFilteredUsers(filtered);
    }, [deviceFilter, users]);

    const handleSelectUser = (userId) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(userId)) {
            newSelected.delete(userId);
        } else {
            newSelected.add(userId);
        }
        setSelectedUsers(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedUsers.size === filteredUsers.length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Aldri';
        const date = new Date(dateString);
        return date.toLocaleDateString('nb-NO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        
        if (selectedUsers.size === 0) {
            setError('Vennligst velg minst én bruker');
            return;
        }

        if (!emailSubject.trim() || !emailContent.trim()) {
            setError('Emne og innhold er påkrevd');
            return;
        }

        try {
            setSending(true);
            setError('');
            setMessage('');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/email/send-bcc`,
                {
                    userIds: Array.from(selectedUsers),
                    subject: emailSubject,
                    html: emailContent
                },
                { withCredentials: true }
            );

            setMessage(`E-post sendt til ${response.data.recipientsCount} brukere`);
            setEmailSubject('');
            setEmailContent('');
            setSelectedUsers(new Set());
        } catch (error) {
            console.error('Error sending email:', error);
            setError(error.response?.data?.message || 'Kunne ikke sende e-post');
        } finally {
            setSending(false);
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return '⇅';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    return (
        <div className="email-management-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%'
                }}>
                    <h1>E-post administrasjon</h1>
                    <button 
                        onClick={logout}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <i className="fas fa-sign-out-alt"></i> Logg ut
                    </button>
                </div>
            </div>

            {message && <div className="success-message">{message}</div>}
            {error && <div className="error-message">{error}</div>}

            <div className="email-management-grid">
                {/* Email Composition Section */}
                <div className="email-composition-section">
                    <h2>Send e-post</h2>
                    <form onSubmit={handleSendEmail} className="email-form">
                        <div className="form-group">
                            <label htmlFor="emailSubject">Emne *</label>
                            <input
                                type="text"
                                id="emailSubject"
                                value={emailSubject}
                                onChange={(e) => setEmailSubject(e.target.value)}
                                placeholder="E-post emne"
                                required
                                disabled={sending}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="emailContent">Innhold (HTML) *</label>
                            <textarea
                                id="emailContent"
                                value={emailContent}
                                onChange={(e) => setEmailContent(e.target.value)}
                                placeholder="E-post innhold (HTML format)"
                                rows="10"
                                required
                                disabled={sending}
                            />
                        </div>
                        <div className="selected-count">
                            <strong>{selectedUsers.size}</strong> brukere valgt
                        </div>
                        <button 
                            type="submit" 
                            className="send-email-button"
                            disabled={sending || selectedUsers.size === 0}
                        >
                            {sending ? (
                                <>
                                    <i className="fas fa-spinner fa-spin"></i> Sender...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-paper-plane"></i> Send e-post (BCC)
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Users List Section */}
                <div className="users-list-section">
                    <div className="users-list-header">
                        <h2>Brukere med e-post ({filteredUsers.length})</h2>
                        <div className="list-controls">
                            <div className="filter-control">
                                <label htmlFor="deviceFilter">Filtrer etter enhet:</label>
                                <select
                                    id="deviceFilter"
                                    value={deviceFilter}
                                    onChange={(e) => setDeviceFilter(e.target.value)}
                                >
                                    <option value="all">Alle</option>
                                    <option value="android">Android</option>
                                    <option value="ios">iOS</option>
                                    <option value="unknown">Ukjent</option>
                                </select>
                            </div>
                            <button
                                onClick={handleSelectAll}
                                className="select-all-button"
                            >
                                {selectedUsers.size === filteredUsers.length ? 'Fjern alle' : 'Velg alle'}
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Laster brukere...</p>
                        </div>
                    ) : (
                        <div className="users-table-container">
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th className="checkbox-column">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        <th>Navn</th>
                                        <th>E-post</th>
                                        <th 
                                            className="sortable"
                                            onClick={() => handleSort('last_logged_in')}
                                        >
                                            Sist innlogget {getSortIcon('last_logged_in')}
                                        </th>
                                        <th 
                                            className="sortable"
                                            onClick={() => handleSort('device')}
                                        >
                                            Enhet {getSortIcon('device')}
                                        </th>
                                        <th>Rolle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="no-users">
                                                Ingen brukere funnet
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <tr 
                                                key={user.id}
                                                className={selectedUsers.has(user.id) ? 'selected' : ''}
                                            >
                                                <td className="checkbox-column">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.has(user.id)}
                                                        onChange={() => handleSelectUser(user.id)}
                                                    />
                                                </td>
                                                <td>{user.name || 'Ingen navn'}</td>
                                                <td>{user.email}</td>
                                                <td>{formatDate(user.lastLoggedIn)}</td>
                                                <td>
                                                    <span className={`device-badge ${user.device}`}>
                                                        {user.device === 'android' && <i className="fab fa-android"></i>}
                                                        {user.device === 'ios' && <i className="fab fa-apple"></i>}
                                                        {(!user.device || user.device === 'unknown') && <i className="fas fa-question"></i>}
                                                        {user.device === 'android' ? 'Android' : 
                                                         user.device === 'ios' ? 'iOS' : 'Ukjent'}
                                                    </span>
                                                </td>
                                                <td>{user.role}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

